import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { stackActions } from 'actions/stackActions';
import { makeStyles } from '@material-ui/core/styles';
import {
  Divider,
  Typography,
  Button,
  FormControlLabel,
  Switch,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
} from '@material-ui/core';
import { PageToolbar } from 'components/PageToolbar';
import { StacksTable } from 'features/stacks/StacksTable';
import {
  DateRangePickerComponent,
  now,
  lastMonth,
} from 'components/DateRangePicker';
import { ApplicationState } from 'store';
import { StacksMap } from 'features/stacks/StacksMap';
import { getAuthorizedDepartments } from 'helpers/authorizedDepartments';

import FilterListIcon from '@material-ui/icons/FilterList';
import SearchIcon from '@material-ui/icons/Search';

import OverlaySpinner from 'components/spinners/OverlaySpinner';
import FilterDrawer from 'components/FilterDrawer';
import MultiSelectFilter from 'components/MultiSelectFilter';
import {
  TranslatedFilterOptions,
  translateFilterOptions,
} from 'helpers/filterOptions';
import { Autocomplete } from 'components/Autocomplete';
import RangeFilter from 'components/RangeFilter';
import SelectFilter from 'components/SelectFilter';
import { sawmillActions } from 'actions/sawmillActions';
import { StackDateTypeDto, StackStateEnumDto } from '.generated/api';
import { GetApp } from '@material-ui/icons';
import IconSpinner from 'components/spinners/ButtonSpinner';
import { IStackQuery } from '../store/models/IStackQuery';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: '100%',
  },
  toolbar: {
    display: 'flex',
  },
  filterContainer: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  filterRow: {
    marginTop: theme.spacing(1),
  },
  overlaySpinner: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
  },
  datePickerContainer: {
    padding: theme.spacing(2),
  },
  flexGrow: {
    flexGrow: 1,
  },
  map: {
    width: '100%',
    height: '33vh',
    minHeight: '33vh',
  },
  table: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: '#ffffff',
  },
  filterSelect: {
    width: '18rem',
  },
  filterButtonRoot: {
    margin: 0,
    minWidth: 0,
    padding: 14,
  },
  filtersApplied: {
    marginLeft: theme.spacing(2),
  },
  selectContainer: {
    maxWidth: '100%', // ensures ellipsis in select dropdown
  },
}));

const stringToBool = (s?: string): boolean | undefined => {
  if (s === 'true') {
    return true;
  }
  if (s === 'false') {
    return false;
  }
  return undefined;
};

const boolToString = (b?: boolean) => {
  if (b === true) {
    return 'true';
  }
  if (b === false) {
    return 'false';
  }
  return '';
};

interface ParamTypes {
  orgId: string;
  deptId: string;
}

const DEBOUNCE_TIME = 300;

const Stacks: React.FC = () => {
  const defaultPageSize = 50;
  const { t } = useTranslation(['translations']);
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { orgId, deptId } = useParams<ParamTypes>();
  const [filters, setFilters] = useState<IStackQuery | undefined>(undefined);

  const [translatedFilterOptions, setTranslatedFilterOptions] =
    useState<TranslatedFilterOptions>({});

  const [extendedFiltersVisible, setExtendedFiltersVisible] = useState(false);
  const [departmentIds, setDepartmentIds] = useState<string[]>([]);
  const [showMap, setShowMap] = useState(true);
  const [searchString, setSearchString] = useState('');

  const userAccess = useSelector(
    (state: ApplicationState) => state.isLoggedIn?.userAccess,
  );

  const orgDepartments = useSelector(
    (state: ApplicationState) => state.organization?.organization?.departments,
  );

  const {
    measurementCard,
    stacks,
    filterOptions,
    isLoadingStacks,
    isLoadingCoordinates,
    updatedAt,
    filtersApplied,
    accumulatedCsvDownloadAllProgress,
  } = useSelector((state: ApplicationState) => state.stacks);

  const sawmills = useSelector(
    (state: ApplicationState) => state.sawmills?.sawmills ?? [],
  );

  const [change, setChange] = useState<string>('');

  const firstUpdate = useRef(true);
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    const delayDebounce = setTimeout(() => {
      history.replace({ search: change });
    }, DEBOUNCE_TIME);

    return () => clearTimeout(delayDebounce);
  }, [change]);

  const updateFilter = (name: string, value?: string) => {
    const query = new URLSearchParams(location.search);
    if (value && value.length > 0) {
      query.set(name, value);
    } else {
      query.delete(name);
    }
    setChange(query.toString());
  };

  const updateListFilter = (name: string, values: string[]) => {
    const query = new URLSearchParams(location.search);
    query.delete(name);
    for (const value of values) {
      query.append(name, value);
    }
    setChange(query.toString());
  };

  const updateShowMap = (value: boolean) => {
    const query = new URLSearchParams(location.search);
    query.set('showMap', boolToString(value) ?? '');
    setChange(query.toString());
  };

  const handleAssortmentLengthChange = (value: number[]) => {
    const query = new URLSearchParams(location.search);
    if (
      value[0] === filterOptions?.assortmentLengthMin &&
      value[1] === filterOptions?.assortmentLengthMax
    ) {
      query.delete('assortmentLengthMin');
      query.delete('assortmentLengthMax');
    } else {
      query.set('assortmentLengthMin', value[0].toString());
      query.set('assortmentLengthMax', value[1].toString());
    }
    setChange(query.toString());
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value);
  };

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    updateFilter('search', searchString);
  };

  const handleDateRangeChange = (
    from: Date | null,
    to: Date | null,
    valid: boolean,
  ) => {
    if (!valid) {
      return;
    }

    const query = new URLSearchParams(location.search);
    if (from) {
      query.set('from', from.toISOString());
    } else {
      query.delete('from');
    }

    if (to) {
      query.set('to', to.toISOString());
    } else {
      query.delete('to');
    }

    setChange(query.toString());
  };

  const handlePageChanged = (newPage: number) => {
    const query = new URLSearchParams(location.search);
    if (newPage === 0) {
      query.delete('page'); // default page 1
    } else {
      query.set('page', (newPage + 1).toString());
    }
    history.push({ search: query.toString() });
  };

  const handlePageSizeChanged = (pageSize: number) => {
    const query = new URLSearchParams(location.search);
    query.delete('page'); // default page 1
    if (pageSize === defaultPageSize) {
      query.delete('pageSize');
    } else {
      query.set('pageSize', pageSize.toString());
    }
    history.push({ search: query.toString() });
  };

  // Set default filter values in stack overview
  useEffect(() => {
    if (location.search === '') {
      const searchParams = new URLSearchParams();
      searchParams.set('dateType', StackDateTypeDto.INVOICEABLE);
      searchParams.set('from', lastMonth().toISOString());
      searchParams.set('to', now().toISOString());
      location.search = searchParams.toString();
    }
  }, [location]);

  const handleResetFilters = () => {
    setChange('');
  };

  const handleShowMapChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateShowMap(event.target.checked);
  };

  function handleDownloadAccumulatedCsvClick() {
    if (measurementCard) {
      dispatch(
        stackActions.downloadAllAccumulatedCsv(measurementCard.id, {
          ...filters,
          departmentIds: departmentIds,
        }),
      );
    }
  }

  // fetch measurement card and sawmills
  useEffect(() => {
    if (orgId) {
      dispatch(stackActions.getOrganizationMeasurementCard(orgId));
      dispatch(sawmillActions.getSawmillsByOrganizationId(orgId));
    }
  }, [orgId]);

  // translate filter options
  useEffect(() => {
    if (measurementCard && filterOptions) {
      setTranslatedFilterOptions(
        translateFilterOptions(measurementCard, filterOptions),
      );
    } else {
      setTranslatedFilterOptions({});
    }
  }, [measurementCard, filterOptions]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const dateType = query.get('dateType') ?? undefined;
    const from = query.get('from') ?? undefined;
    const to = query.get('to') ?? undefined;
    const states = query.getAll('states');
    const sawmillIds = query.getAll('sawmillIds');
    const registrators = query.getAll('registrators');
    const surveyors = query.getAll('surveyors');
    const treeSpecies = query.getAll('treeSpecies');
    const assortments = query.getAll('assortments');
    const assortmentLengthMin = query.get('assortmentLengthMin') ?? undefined;
    const assortmentLengthMax = query.get('assortmentLengthMax') ?? undefined;
    const areaNumber = query.get('areaNumber') ?? undefined;
    const fullMeasurement = query.get('fullMeasurement') ?? undefined;
    const syncWithLesnikPlus = query.get('syncWithLesnikPlus') ?? undefined;
    const search = query.get('search') ?? undefined;
    const page = query.get('page') ?? undefined;
    const pageSize = query.get('pageSize') ?? undefined;
    const showMapValue = query.get('showMap') ?? 'true';

    setSearchString(search ?? '');
    setShowMap(JSON.parse(showMapValue));

    let queryChanged = false;

    if (!dateType) {
      query.set('dateType', StackDateTypeDto.INVOICEABLE);
      queryChanged = true;
    }

    if (!from && !to) {
      // default date filter is last month
      query.set('from', lastMonth().toISOString());
      query.set('to', now().toISOString());
      queryChanged = true;
    }

    if (queryChanged) {
      setChange(query.toString());
    }

    // set filters from query params
    setFilters({
      departmentIds: [], // TODO: DHM: Test if this works
      dateType: dateType ? (dateType as StackDateTypeDto) : undefined,
      from,
      to,
      sawmillIds,
      states: states
        ? states.map((state) => {
            return state as StackStateEnumDto;
          })
        : undefined,
      registrators,
      surveyors,
      treeSpecies,
      assortments,
      assortmentLengthMin: assortmentLengthMin
        ? parseFloat(assortmentLengthMin)
        : undefined,
      assortmentLengthMax: assortmentLengthMax
        ? parseFloat(assortmentLengthMax)
        : undefined,
      areaNumber: areaNumber ? parseInt(areaNumber) : undefined,
      fullMeasurement: stringToBool(fullMeasurement),
      syncWithLesnikPlus: stringToBool(syncWithLesnikPlus),
      search,
      page: page ? parseInt(page) : undefined,
      pageSize: pageSize ? parseInt(pageSize) : defaultPageSize,
      showMap: showMapValue,
    });
  }, [location]);

  useEffect(() => {
    if (filters && departmentIds) {
      dispatch(
        stackActions.getStacks({ ...filters, departmentIds: departmentIds }),
      );
    }
  }, [filters, departmentIds]);

  useEffect(() => {
    if (userAccess && orgDepartments) {
      if (deptId === 'all') {
        const departments = getAuthorizedDepartments(
          orgId,
          userAccess,
          orgDepartments,
          userAccess.departments,
        );

        setDepartmentIds(
          departments
            ? departments.map((department) => department.departmentId)
            : [],
        );
      } else {
        setDepartmentIds(deptId ? [deptId] : []);
      }
    }
  }, [deptId, userAccess, orgDepartments]);

  return (
    <div className={classes.root}>
      <PageToolbar>
        <div className={classes.toolbar}>
          <Typography variant="subtitle1">{t('stacks_page.title')}</Typography>
          <div className={classes.flexGrow}></div>
          <FormControlLabel
            control={
              <Switch checked={showMap} onChange={handleShowMapChange} />
            }
            label={t('stacks_page.show_map')}
          />
        </div>
        <Divider />
        <div className={classes.filterContainer}>
          <Grid
            container
            spacing={2}
            alignItems="center"
            className={classes.filterRow}
          >
            <Grid item className={classes.selectContainer}>
              <SelectFilter
                onSelect={(value) => updateFilter('dateType', value)}
                value={filters?.dateType}
                label={t('stacks_page.date_type.title')}
                options={[
                  {
                    value: StackDateTypeDto.PRE_REGISTRATION,
                    label: t('stacks_page.date_type.preregistration'),
                  },
                  {
                    value: StackDateTypeDto.MEASURED,
                    label: t('stacks_page.date_type.measured'),
                  },
                  {
                    value: StackDateTypeDto.INVOICEABLE,
                    label: t('stacks_page.date_type.invoiceable'),
                  },
                  {
                    value: StackDateTypeDto.ASSIGNED_TO_SAWMILL,
                    label: t('stacks_page.date_type.assigned_to_sawmill'),
                  },
                  {
                    value: StackDateTypeDto.LAST_CHANGED,
                    label: t('stacks_page.date_type.last_changed'),
                  },
                ]}
                showAllOption={false}
              />
            </Grid>
            <DateRangePickerComponent
              itemClass={classes.filterSelect}
              clearable
              startDate={filters?.from ? new Date(filters.from) : null}
              endDate={filters?.to ? new Date(filters.to) : null}
              onChange={handleDateRangeChange}
            />
            <Grid item className={classes.filterSelect}>
              <form onSubmit={handleSearchSubmit}>
                <TextField
                  name="search"
                  fullWidth
                  variant="outlined"
                  label={t('stacks_page.search')}
                  onChange={handleSearchChange}
                  value={searchString}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton type="submit">
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </form>
            </Grid>
            <Grid item>
              <Button
                classes={{ root: classes.filterButtonRoot }}
                size="large"
                variant={extendedFiltersVisible ? 'contained' : 'outlined'}
                color="secondary"
                onClick={() =>
                  setExtendedFiltersVisible(!extendedFiltersVisible)
                }
              >
                <FilterListIcon />
              </Button>
              {!!filtersApplied && (
                <span className={classes.filtersApplied}>
                  {t('stacks_page.n_filters_applied', {
                    count: filtersApplied,
                  })}
                </span>
              )}
            </Grid>
            <div className={classes.flexGrow}></div>
            <Grid item>
              <Button
                disabled={accumulatedCsvDownloadAllProgress !== undefined}
                onClick={handleDownloadAccumulatedCsvClick}
                size="large"
                color="secondary"
                variant="outlined"
                startIcon={
                  <IconSpinner
                    isLoading={!!accumulatedCsvDownloadAllProgress}
                    progress={accumulatedCsvDownloadAllProgress}
                  >
                    <GetApp />
                  </IconSpinner>
                }
              >
                {t('stacks_page.table.download_all_stacks')}
              </Button>
            </Grid>
          </Grid>
        </div>
        {t('stacks_page.showing_n_results', {
          count: stacks ? stacks.data.length : 0,
        })}
        {updatedAt && ` - ${updatedAt.toLocaleString()}`}
      </PageToolbar>
      <OverlaySpinner
        isLoading={isLoadingStacks === true || isLoadingCoordinates === true}
        className={classes.overlaySpinner}
      >
        {showMap && (
          <div className={classes.map}>
            <StacksMap />
          </div>
        )}
        <div className={classes.table}>
          <StacksTable
            onPageChanged={handlePageChanged}
            onPageSizeChanged={handlePageSizeChanged}
          />
        </div>
      </OverlaySpinner>
      <FilterDrawer
        open={extendedFiltersVisible}
        onResetFilters={handleResetFilters}
        onClose={() => {
          setExtendedFiltersVisible(false);
        }}
      >
        <Grid container spacing={2} direction="column">
          <Grid item className={classes.selectContainer}>
            <MultiSelectFilter
              values={filters?.states ?? undefined}
              onSelect={(values) => updateListFilter('states', values)}
              // onSelect={handleStatusChange}
              label={t('stacks_page.table.status')}
              items={[
                {
                  value: 'AssignedToSawmill',
                  label: t('stacks_page.table.assigned_to_sawmill'),
                },
                {
                  value: 'PreRegistration',
                  label: t('stacks_page.table.not_measured'),
                },
                {
                  value: 'Measured',
                  label: t('stacks_page.table.measured'),
                },
              ]}
            />
          </Grid>

          {translatedFilterOptions.registrators && (
            <Grid item className={classes.selectContainer}>
              <MultiSelectFilter
                onSelect={(values) => updateListFilter('registrators', values)}
                values={filters?.registrators ?? undefined}
                label={translatedFilterOptions.registrators.title}
                items={translatedFilterOptions.registrators.options}
              />
            </Grid>
          )}

          {translatedFilterOptions.surveyors && (
            <Grid item className={classes.selectContainer}>
              <MultiSelectFilter
                onSelect={(values) => updateListFilter('surveyors', values)}
                values={filters?.surveyors ?? undefined}
                label={translatedFilterOptions.surveyors.title}
                items={translatedFilterOptions.surveyors.options}
              />
            </Grid>
          )}

          {translatedFilterOptions.treeSpecies && (
            <Grid item className={classes.selectContainer}>
              <MultiSelectFilter
                onSelect={(values) => updateListFilter('treeSpecies', values)}
                values={filters?.treeSpecies ?? undefined}
                label={translatedFilterOptions.treeSpecies.title}
                items={translatedFilterOptions.treeSpecies.options}
              />
            </Grid>
          )}

          {translatedFilterOptions.assortments && (
            <Grid item className={classes.selectContainer}>
              <MultiSelectFilter
                onSelect={(values) => updateListFilter('assortments', values)}
                values={filters?.assortments ?? undefined}
                label={translatedFilterOptions.assortments.title}
                items={translatedFilterOptions.assortments.options}
              />
            </Grid>
          )}

          {translatedFilterOptions.fullMeasurement && (
            <Grid item className={classes.selectContainer}>
              <SelectFilter
                onSelect={(value) => updateFilter('fullMeasurement', value)}
                value={boolToString(filters?.fullMeasurement ?? undefined)}
                label={translatedFilterOptions.fullMeasurement.title}
                options={[
                  {
                    value: boolToString(true),
                    label: translatedFilterOptions.fullMeasurement.trueLabel,
                  },
                  {
                    value: boolToString(false),
                    label: translatedFilterOptions.fullMeasurement.falseLabel,
                  },
                ]}
              />
            </Grid>
          )}

          {translatedFilterOptions.syncWithLesnikPlus && (
            <Grid item className={classes.selectContainer}>
              <SelectFilter
                onSelect={(value) => updateFilter('syncWithLesnikPlus', value)}
                value={boolToString(filters?.syncWithLesnikPlus ?? undefined)}
                label={translatedFilterOptions.syncWithLesnikPlus.title}
                options={[
                  {
                    value: boolToString(true),
                    label: translatedFilterOptions.syncWithLesnikPlus.trueLabel,
                  },
                  {
                    value: boolToString(false),
                    label:
                      translatedFilterOptions.syncWithLesnikPlus.falseLabel,
                  },
                ]}
              />
            </Grid>
          )}

          <Grid item className={classes.selectContainer}>
            <MultiSelectFilter
              onSelect={(values) => updateListFilter('sawmillIds', values)}
              values={filters?.sawmillIds ?? undefined}
              label={t('stacks_page.sawmill')}
              items={sawmills.map((sawmill) => ({
                label: sawmill.name,
                value: sawmill.id,
              }))}
            />
          </Grid>

          {translatedFilterOptions.areaNumber && (
            <Grid item className={classes.selectContainer}>
              <Autocomplete
                label={translatedFilterOptions.areaNumber.title}
                // options={[12345, 1, 2, 3, 4, 5, 123, 1234]}
                options={filterOptions?.areaNumber ?? []}
                getOptionLabel={(option: number) => option.toString()}
                // onSelect={handleAreaNumberSelect}
                onSelect={(value) =>
                  updateFilter(
                    'areaNumber',
                    value !== undefined ? value.toString() : undefined,
                  )
                }
                value={
                  filters?.areaNumber != null ? filters.areaNumber : undefined
                }
              />
            </Grid>
          )}

          {translatedFilterOptions.assortmentLength && (
            <Grid item className={classes.selectContainer}>
              <RangeFilter
                disabledText={t('stacks_page.no_lengths_to_filter_by')}
                label={translatedFilterOptions.assortmentLength.title}
                value={
                  filters?.assortmentLengthMin != null &&
                  filters?.assortmentLengthMax != null
                    ? [filters.assortmentLengthMin, filters.assortmentLengthMax]
                    : undefined
                }
                min={
                  filterOptions?.assortmentLengthMin != null
                    ? filterOptions.assortmentLengthMin
                    : undefined
                }
                max={
                  filterOptions?.assortmentLengthMax != null
                    ? filterOptions.assortmentLengthMax
                    : undefined
                }
                // min={0}
                // max={10}
                step={0.01}
                onChange={handleAssortmentLengthChange}
              />
            </Grid>
          )}
        </Grid>
      </FilterDrawer>
    </div>
  );
};

export { Stacks };
